import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useAuth } from '../../hooks/useAuth';
import { updateUserProfile } from '../../services/userService';
import { updateUserPassword, linkEmailPassword } from '../../services/authService';
import { useLanguage } from '../../contexts/LanguageContext';
import CrossPlatformAlert from '../../components/CrossPlatformAlert';
import { logAuditEvent } from '../../services/auditService';

export default function ProfileScreen() {
  const { appUser, firebaseUser } = useAuth();
  const { t } = useLanguage();

  const [firstname, setFirstname] = useState(appUser?.firstname ?? '');
  const [lastname, setLastname] = useState(appUser?.lastname ?? '');
  const [savingProfile, setSavingProfile] = useState(false);

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);

  useEffect(() => {
    if (appUser) {
      setFirstname(appUser.firstname ?? '');
      setLastname(appUser.lastname ?? '');
    }
  }, [appUser]);

  const hasEmailProvider = firebaseUser?.providerData.some(
    p => p.providerId === 'password',
  ) ?? false;

  const handleSaveProfile = async () => {
    if (!firstname.trim()) {
      CrossPlatformAlert.alert(t('common.validation'), t('profile.firstnameRequired'));
      return;
    }
    if (!appUser) return;
    setSavingProfile(true);
    try {
      await updateUserProfile(appUser.uid, firstname.trim(), lastname.trim());
      await logAuditEvent({
        entityType: 'user',
        entityId: appUser.uid,
        action: 'update',
        details: 'Profile updated',
        previousValues: { firstname: appUser.firstname, lastname: appUser.lastname },
        newValues: { firstname: firstname.trim(), lastname: lastname.trim() },
      });
      CrossPlatformAlert.alert(t('common.done'), t('profile.profileSaved'));
    } catch {
      CrossPlatformAlert.alert(t('common.error'), t('profile.profileSaveError'));
    } finally {
      setSavingProfile(false);
    }
  };

  const validateNewPassword = (): boolean => {
    if (!newPassword) {
      CrossPlatformAlert.alert(t('common.validation'), t('profile.newPasswordRequired'));
      return false;
    }
    if (newPassword.length < 6) {
      CrossPlatformAlert.alert(t('common.validation'), t('profile.newPasswordTooShort'));
      return false;
    }
    if (newPassword !== confirmPassword) {
      CrossPlatformAlert.alert(t('common.validation'), t('profile.passwordMismatch'));
      return false;
    }
    return true;
  };

  const handleSetPassword = async () => {
    if (!validateNewPassword()) return;
    setSavingPassword(true);
    try {
      await linkEmailPassword(newPassword);
      setNewPassword('');
      setConfirmPassword('');
      CrossPlatformAlert.alert(t('common.done'), t('profile.passwordSet'));
    } catch (error: unknown) {
      const code = (error as { code?: string })?.code;
      let message = t('profile.passwordSetError');
      if (code === 'auth/weak-password') {
        message = t('profile.passwordWeakError');
      } else if (code === 'auth/provider-already-linked' || code === 'auth/credential-already-in-use') {
        message = t('profile.passwordAlreadyLinked');
      }
      CrossPlatformAlert.alert(t('common.error'), message);
    } finally {
      setSavingPassword(false);
    }
  };

  const handleChangePassword = async () => {
    if (!currentPassword) {
      CrossPlatformAlert.alert(t('common.validation'), t('profile.currentPasswordRequired'));
      return;
    }
    if (!validateNewPassword()) return;
    setSavingPassword(true);
    try {
      await updateUserPassword(currentPassword, newPassword);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      CrossPlatformAlert.alert(t('common.done'), t('profile.passwordChanged'));
    } catch (error: unknown) {
      let message = t('profile.passwordChangeError');
      if (
        (error as { code?: string })?.code === 'auth/wrong-password' ||
        (error as { code?: string })?.code === 'auth/invalid-credential'
      ) {
        message = t('login.invalidCredentials');
      }
      CrossPlatformAlert.alert(t('common.error'), message);
    } finally {
      setSavingPassword(false);
    }
  };

  if (!appUser) {
    return (
      <View style={styles.center}>
        <ActivityIndicator size="large" color="#0066CC" />
      </View>
    );
  }

  return (
    <ScrollView style={styles.container} contentContainerStyle={styles.content}>
      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <Ionicons name="person-circle-outline" size={24} color="#0066CC" />
          <Text style={styles.cardTitle}>{t('profile.title')}</Text>
        </View>

        <Text style={styles.emailLabel}>{appUser.email}</Text>

        <Text style={styles.label}>{t('profile.firstnameLabel')}</Text>
        <TextInput
          style={styles.input}
          placeholder={t('profile.firstnamePlaceholder')}
          placeholderTextColor="#94A3B8"
          value={firstname}
          onChangeText={setFirstname}
          autoCapitalize="words"
          textContentType="givenName"
        />

        <Text style={styles.label}>{t('profile.lastnameLabel')}</Text>
        <TextInput
          style={styles.input}
          placeholder={t('profile.lastnamePlaceholder')}
          placeholderTextColor="#94A3B8"
          value={lastname}
          onChangeText={setLastname}
          autoCapitalize="words"
          textContentType="familyName"
        />

        <TouchableOpacity
          style={[styles.button, savingProfile && styles.buttonDisabled]}
          onPress={handleSaveProfile}
          disabled={savingProfile}
        >
          {savingProfile ? (
            <ActivityIndicator size="small" color="#FFFFFF" />
          ) : (
            <Text style={styles.buttonText}>{t('profile.saveProfile')}</Text>
          )}
        </TouchableOpacity>
      </View>

      <View style={styles.card}>
        <View style={styles.cardHeader}>
          <Ionicons name="lock-closed-outline" size={24} color="#0066CC" />
          <Text style={styles.cardTitle}>{t('profile.changePassword')}</Text>
        </View>

        {!hasEmailProvider ? (
          <>
            <Text style={styles.infoText}>{t('profile.setPasswordDescription')}</Text>

            <Text style={styles.label}>{t('profile.newPasswordLabel')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('profile.newPasswordPlaceholder')}
              placeholderTextColor="#94A3B8"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              textContentType="newPassword"
              autoComplete="new-password"
            />

            <Text style={styles.label}>{t('profile.confirmPasswordLabel')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('profile.confirmPasswordPlaceholder')}
              placeholderTextColor="#94A3B8"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              textContentType="newPassword"
              autoComplete="new-password"
            />

            <TouchableOpacity
              style={[styles.button, savingPassword && styles.buttonDisabled]}
              onPress={handleSetPassword}
              disabled={savingPassword}
            >
              {savingPassword ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>{t('profile.setPassword')}</Text>
              )}
            </TouchableOpacity>
          </>
        ) : (
          <>
            <Text style={styles.label}>{t('profile.currentPasswordLabel')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('profile.currentPasswordPlaceholder')}
              placeholderTextColor="#94A3B8"
              value={currentPassword}
              onChangeText={setCurrentPassword}
              secureTextEntry
              textContentType="password"
              autoComplete="current-password"
            />

            <Text style={styles.label}>{t('profile.newPasswordLabel')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('profile.newPasswordPlaceholder')}
              placeholderTextColor="#94A3B8"
              value={newPassword}
              onChangeText={setNewPassword}
              secureTextEntry
              textContentType="newPassword"
              autoComplete="new-password"
            />

            <Text style={styles.label}>{t('profile.confirmPasswordLabel')}</Text>
            <TextInput
              style={styles.input}
              placeholder={t('profile.confirmPasswordPlaceholder')}
              placeholderTextColor="#94A3B8"
              value={confirmPassword}
              onChangeText={setConfirmPassword}
              secureTextEntry
              textContentType="newPassword"
              autoComplete="new-password"
            />

            <TouchableOpacity
              style={[styles.button, savingPassword && styles.buttonDisabled]}
              onPress={handleChangePassword}
              disabled={savingPassword}
            >
              {savingPassword ? (
                <ActivityIndicator size="small" color="#FFFFFF" />
              ) : (
                <Text style={styles.buttonText}>{t('profile.changePassword')}</Text>
              )}
            </TouchableOpacity>
          </>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  content: {
    padding: 20,
    paddingBottom: 40,
    maxWidth: 640,
    width: '100%',
    alignSelf: 'center',
  },
  center: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 20,
    marginBottom: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.08,
    shadowRadius: 8,
    elevation: 3,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  cardHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
    gap: 8,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: '700',
    color: '#1E293B',
  },
  emailLabel: {
    fontSize: 14,
    color: '#64748B',
    marginBottom: 16,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
    marginBottom: 6,
    marginTop: 8,
  },
  input: {
    borderWidth: 1,
    borderColor: '#CBD5E1',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#1E293B',
    backgroundColor: '#F8FAFC',
  },
  button: {
    backgroundColor: '#0066CC',
    paddingVertical: 14,
    borderRadius: 8,
    alignItems: 'center',
    marginTop: 20,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  infoText: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 22,
  },
});
